/**
 * selectors/viewHelpers.ts
 *
 * Shared display-mapping helpers for MER Algo Footprints.
 *
 * Extracts repeated business-rule display logic from UI components:
 *   - age/time formatting
 *   - readiness tier from raw score
 *   - cooldown display
 *   - thesis freshness
 *
 * These are pure functions — no side effects, no imports from React.
 * Keeps UI components thin; keeps display logic testable and consistent.
 */

import { ReadinessTier } from '../../risk/riskEngine';

// ─── Age / elapsed time ───────────────────────────────────────────────────────

/**
 * Human-readable elapsed-time label from a Unix ms timestamp.
 * Returns '—' when ts is null/undefined.
 * Examples: '< 1m', '3m', '2h'
 */
export function formatAge(ts: number | null | undefined): string {
  if (ts == null) return '—';
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60)    return '< 1m';
  if (s < 3600)  return `${Math.floor(s / 60)}m`;
  return `${Math.floor(s / 3600)}h`;
}

/**
 * Returns 'just now' for sub-minute, else '<n>m ago'.
 * Used in panels data-freshness labels.
 */
export function formatAgoShort(ts: number | null | undefined): string {
  if (ts == null) return '—';
  const age = Math.round((Date.now() - ts) / 60000);
  return age < 1 ? 'just now' : `${age}m ago`;
}

// ─── Readiness tier ───────────────────────────────────────────────────────────

/**
 * Derive ReadinessTier from a raw numeric readiness score (0-100).
 * Mirrors the thresholds in riskEngine.ts and dashboardView.ts.
 */
export function readinessTierFromScore(score: number): ReadinessTier {
  if (score >= 80) return 'urgent';
  if (score >= 65) return 'ready';
  if (score >= 45) return 'good';
  return 'interesting';
}

/**
 * Colour for a raw numeric readiness score (shorthand of READINESS_COLORS).
 */
export function readinessColorFromScore(score: number): string {
  if (score >= 70) return 'var(--green)';
  if (score >= 45) return 'var(--amber)';
  return 'var(--blue-dim)';
}

// ─── Cooldown display ─────────────────────────────────────────────────────────

/**
 * Remaining cooldown in seconds from a last-updated timestamp and a window (ms).
 * Returns 0 when not on cooldown.
 */
export function cooldownRemainingSec(lastUpdatedAt: number | null, windowMs: number): number {
  if (!lastUpdatedAt) return 0;
  return Math.max(0, Math.round((windowMs - (Date.now() - lastUpdatedAt)) / 1000));
}

// ─── Thesis freshness ─────────────────────────────────────────────────────────

/**
 * Returns true when the thesis input hash matches the current context hash,
 * indicating the thesis is still fresh for the current market state.
 */
export function isThesisFresh(
  lastInputHash: string | null,
  currentInputHash: string
): boolean {
  return lastInputHash === currentInputHash;
}

// ─── Exhaustiveness guard ─────────────────────────────────────────────────────

/**
 * Exhaustiveness helper — call in the default branch of a switch on a union.
 * TypeScript will error at compile time if a case is unhandled.
 * At runtime, throws an error with the unexpected value.
 */
export function assertUnreachable(x: never, label = 'Unhandled case'): never {
  throw new Error(`${label}: ${String(x)}`);
}
